// Ads page (SPEC §4): the organized Google Ads section — manual monthly spend
// entry (upsert_ad_spend RPC), a summary strip, the cohort-ROAS months table
// from v_google_ads_monthly (empty pre-spend months hidden behind a toggle),
// a Google Ads clients-by-cohort-month breakdown from v_client_list, and a
// nudge to attribute recent clients that have no acquisition source.

import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { useQuery } from "../lib/useQuery";
import { money } from "../lib/format";
import { maskMoney, usePrivacy } from "../lib/privacy";
import { fmtDate } from "../lib/dates";
import DataTable from "../components/DataTable";
import type { Column } from "../components/DataTable";
import { SegmentChip, WhaleBadge } from "../components/Badges";
import type { AdsMonthly, ClientRow } from "../lib/types";

const NY_TZ = "America/New_York";

/** YYYY-MM-DD of (today − days) as an NY business date. */
function nyYmdDaysAgo(days: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: NY_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(Date.now() - days * 86_400_000));
}

/** "2026-08-01" (or any string starting YYYY-MM) → "Aug 2026". */
function monthLabel(m: string | null | undefined): string {
  if (!m) return "—";
  const match = /^(\d{4})-(\d{2})/.exec(m);
  if (!match) return m;
  const d = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, 1));
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "UTC",
    month: "short",
    year: "numeric",
  }).format(d);
}

/** PostgREST can serialize numerics as strings; coalesce to a real number or null. */
function num(v: number | string | null | undefined): number | null {
  if (v == null) return null;
  const n = typeof v === "string" ? Number(v) : v;
  return Number.isFinite(n) ? n : null;
}

function roasDisplay(v: number | string | null | undefined): string {
  const n = num(v);
  if (n == null) return "—";
  return `${n.toFixed(2)}×`;
}

/**
 * Months-table columns. Built per privacy state: dollar cells (spend, cohort
 * revenue, CACs) mask to "$ ••••" in privacy mode; counts, months, and ROAS
 * ratios stay visible. Sorting always uses the real values.
 */
function buildColumns(privacy: boolean): Column<AdsMonthly>[] {
  const dollars = (v: number | string | null | undefined) => maskMoney(money(v), privacy);
  return [
    {
      key: "month",
      label: "Month",
      render: (r) => <span className="nowrap">{monthLabel(r.month)}</span>,
      sortValue: (r) => r.month,
      width: "110px",
    },
    {
      key: "spend",
      label: "Spend",
      align: "right",
      render: (r) => dollars(r.spend),
      sortValue: (r) => num(r.spend),
    },
    {
      key: "new_google_clients",
      label: "New Google-Ads clients",
      align: "right",
      render: (r) => num(r.new_google_clients) ?? 0,
      sortValue: (r) => num(r.new_google_clients),
    },
    {
      key: "cohort_revenue_to_date",
      label: "Cohort revenue to date",
      align: "right",
      render: (r) => dollars(r.cohort_revenue_to_date),
      sortValue: (r) => num(r.cohort_revenue_to_date),
    },
    {
      key: "roas",
      label: "ROAS",
      align: "right",
      render: (r) => roasDisplay(r.roas),
      sortValue: (r) => num(r.roas),
    },
    {
      key: "cac",
      label: "CAC",
      align: "right",
      render: (r) => dollars(r.cac),
      sortValue: (r) => num(r.cac),
    },
    {
      key: "blended_cac",
      label: "Blended CAC",
      align: "right",
      render: (r) => dollars(r.blended_cac),
      sortValue: (r) => num(r.blended_cac),
    },
  ];
}

/** A month is "empty" pre-spend/pre-attribution noise: no spend, no GA clients. */
function isEmptyMonth(r: AdsMonthly): boolean {
  return (num(r.spend) ?? 0) === 0 && (num(r.new_google_clients) ?? 0) === 0;
}

/** The v_client_list fields the cohort breakdown uses. */
type GaClient = Pick<
  ClientRow,
  "id" | "full_name" | "first_visit" | "ltv" | "rfm_segment" | "is_whale"
>;

interface CohortGroup {
  /** "YYYY-MM", or null for the attributed-but-never-visited bucket. */
  monthKey: string | null;
  clients: GaClient[];
  lifetime: number;
}

/** Group GA clients by first-visit month (desc); no-visit clients last. */
function groupByCohortMonth(clients: GaClient[]): CohortGroup[] {
  const byMonth = new Map<string, GaClient[]>();
  const noVisit: GaClient[] = [];
  for (const c of clients) {
    const key = c.first_visit ? c.first_visit.slice(0, 7) : null;
    if (key == null) {
      noVisit.push(c);
      continue;
    }
    const list = byMonth.get(key);
    if (list) list.push(c);
    else byMonth.set(key, [c]);
  }
  const byLtvDesc = (a: GaClient, b: GaClient) => (num(b.ltv) ?? 0) - (num(a.ltv) ?? 0);
  const groups: CohortGroup[] = [...byMonth.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([monthKey, list]) => ({
      monthKey,
      clients: [...list].sort(byLtvDesc),
      lifetime: list.reduce((s, c) => s + (num(c.ltv) ?? 0), 0),
    }));
  if (noVisit.length > 0) {
    groups.push({
      monthKey: null,
      clients: [...noVisit].sort(byLtvDesc),
      lifetime: noVisit.reduce((s, c) => s + (num(c.ltv) ?? 0), 0),
    });
  }
  return groups;
}

function CohortTable({ clients, privacy }: { clients: GaClient[]; privacy: boolean }) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            <th>Client</th>
            <th style={{ width: "130px" }}>First visit</th>
            <th className="right" style={{ width: "110px" }}>
              Lifetime value
            </th>
            <th style={{ width: "180px" }}>Segment</th>
          </tr>
        </thead>
        <tbody>
          {clients.map((c) => (
            <tr key={String(c.id)}>
              <td>
                <Link to={`/clients/${c.id}`}>{c.full_name}</Link>
              </td>
              <td className="nowrap">{fmtDate(c.first_visit)}</td>
              <td className="right">{maskMoney(money(c.ltv), privacy)}</td>
              <td>
                <SegmentChip segment={c.rfm_segment} link />
                <WhaleBadge whale={c.is_whale} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Ads() {
  // Current NY month for the picker default.
  const [month, setMonth] = useState<string>(() => nyYmdDaysAgo(0).slice(0, 7));
  const [amount, setAmount] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formOk, setFormOk] = useState<string | null>(null);
  const [showEmptyMonths, setShowEmptyMonths] = useState(false);
  // Privacy mode (MetricsBar 👁 toggle): dollar figures mask to "$ ••••".
  const [privacy] = usePrivacy();
  const columns = useMemo(() => buildColumns(privacy), [privacy]);

  const months = useQuery<AdsMonthly[]>(async () => {
    const { data, error } = await supabase
      .from("v_google_ads_monthly")
      .select("*")
      .order("month", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as AdsMonthly[];
  }, []);

  // All Google-Ads-attributed clients, for the cohort breakdown + summary strip.
  const gaClients = useQuery<GaClient[]>(async () => {
    const { data, error } = await supabase
      .from("v_client_list")
      .select("id, full_name, first_visit, ltv, rfm_segment, is_whale")
      .eq("acquisition_source", "google_ads")
      .order("ltv", { ascending: false });
    if (error) throw new Error(error.message);
    return (data ?? []) as GaClient[];
  }, []);

  // Nudge: clients with no acquisition_source whose first visit was in the
  // last 90 days (gte on first_visit also excludes never-visited clients).
  const unattributed = useQuery<number>(async () => {
    const { count, error } = await supabase
      .from("v_client_list")
      .select("id", { count: "exact", head: true })
      .is("acquisition_source", null)
      .gte("first_visit", nyYmdDaysAgo(90));
    if (error) throw new Error(error.message);
    return count ?? 0;
  }, []);

  const monthRows = months.data ?? [];
  const emptyMonthCount = useMemo(() => monthRows.filter(isEmptyMonth).length, [monthRows]);
  const visibleMonths = useMemo(
    () => (showEmptyMonths ? monthRows : monthRows.filter((r) => !isEmptyMonth(r))),
    [monthRows, showEmptyMonths],
  );

  // Summary strip. Overall ROAS = Σ cohort revenue of months WITH spend ÷ Σ spend
  // (cohort revenue from unpaid months would inflate the ratio).
  const summary = useMemo(() => {
    let totalSpend = 0;
    let revenueOnSpend = 0;
    let best: AdsMonthly | null = null;
    for (const r of monthRows) {
      const spend = num(r.spend) ?? 0;
      totalSpend += spend;
      if (spend > 0) revenueOnSpend += num(r.cohort_revenue_to_date) ?? 0;
      const roas = num(r.roas);
      if (roas != null && (best == null || roas > (num(best.roas) ?? -Infinity))) best = r;
    }
    const clients = gaClients.data ?? [];
    return {
      totalClients: gaClients.data == null ? null : clients.length,
      totalLifetime:
        gaClients.data == null
          ? null
          : clients.reduce((s, c) => s + (num(c.ltv) ?? 0), 0),
      overallRoas: totalSpend > 0 ? revenueOnSpend / totalSpend : null,
      bestMonth: best,
    };
  }, [monthRows, gaClients.data]);

  const cohorts = useMemo(() => groupByCohortMonth(gaClients.data ?? []), [gaClients.data]);

  async function saveSpend(e: FormEvent) {
    e.preventDefault();
    setFormOk(null);
    if (!/^\d{4}-\d{2}$/.test(month)) {
      setFormError("Pick a month.");
      return;
    }
    const amt = Number(amount);
    if (amount.trim() === "" || !Number.isFinite(amt) || amt < 0) {
      setFormError("Enter a spend amount of $0 or more.");
      return;
    }
    setFormError(null);
    setSaving(true);
    const { error } = await supabase.rpc("upsert_ad_spend", {
      p_month: `${month}-01`,
      p_amount: amt,
    });
    setSaving(false);
    if (error) {
      setFormError(error.message);
      return;
    }
    setFormOk(`Saved ${money(amt)} for ${monthLabel(`${month}-01`)}.`);
    setAmount("");
    months.reload();
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Google Ads</h1>
          <div className="page-sub">
            Manual monthly spend + cohort ROAS. Attribution happens on the client list.
          </div>
        </div>
      </div>

      <div className="home-metric-grid">
        <div className="metric-card grad-sky">
          <div className="metric-card-label">Google Ads clients</div>
          <div className="metric-card-value">{summary.totalClients ?? "—"}</div>
          <div className="metric-card-sub">attributed all-time</div>
        </div>
        <div className="metric-card grad-mint">
          <div className="metric-card-label">GA lifetime revenue</div>
          <div className="metric-card-value">
            {summary.totalLifetime == null ? "—" : maskMoney(money(summary.totalLifetime), privacy)}
          </div>
          <div className="metric-card-sub">everything they ever spent</div>
        </div>
        <div className="metric-card grad-lavender">
          <div className="metric-card-label">Overall ROAS</div>
          <div className="metric-card-value">{roasDisplay(summary.overallRoas)}</div>
          <div className="metric-card-sub">cohort revenue ÷ total spend</div>
        </div>
        <div className="metric-card grad-butter">
          <div className="metric-card-label">Best month</div>
          <div className="metric-card-value">
            {summary.bestMonth ? monthLabel(summary.bestMonth.month) : "—"}
          </div>
          <div className="metric-card-sub">
            {summary.bestMonth ? `${roasDisplay(summary.bestMonth.roas)} ROAS` : "no ROAS yet"}
          </div>
        </div>
      </div>

      <div className="panel mt-2">
        <div className="panel-title">Record monthly spend</div>
        {formError && <div className="error-banner">{formError}</div>}
        {formOk && <div className="success-banner">{formOk}</div>}
        <form onSubmit={saveSpend}>
          <div className="form-row">
            <div className="field">
              <label htmlFor="ads-month">Month</label>
              <input
                id="ads-month"
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="ads-amount">Spend ($)</label>
              <input
                id="ads-amount"
                type="number"
                min="0"
                step="0.01"
                inputMode="decimal"
                placeholder="0.00"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </div>
            <div className="field">
              <label>&nbsp;</label>
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? "Saving…" : "Save spend"}
              </button>
            </div>
          </div>
          <div className="field-hint">
            Replaces any previously entered manual spend for that month (Google Ads → monthly
            invoice total). API spend pull comes in a later phase.
          </div>
        </form>
      </div>

      {unattributed.data != null && unattributed.data > 0 && (
        <div className="info-banner mt-2">
          <span className="bold">{unattributed.data}</span>{" "}
          {unattributed.data === 1 ? "client" : "clients"} with a first visit in the last 90 days{" "}
          {unattributed.data === 1 ? "has" : "have"} no acquisition source — ROAS and CAC undercount
          until they are attributed. On the <Link to="/clients">Clients</Link> list, select the rows
          and use the “Set acquisition source” bulk action: Google Ads for paid clicks, SEO (GBP)
          for organic Google / Maps bookings.
        </div>
      )}

      <div className="panel mt-2">
        <div className="panel-title">Months</div>
        <p className="muted small">
          ROAS is cohort-based: revenue counts ALL purchases ever made by clients whose first visit
          was that month, so a later $1,000 package retroactively lifts that month's ROAS.
        </p>
        {months.error && <div className="error-banner">{months.error}</div>}
        {months.loading && !months.data ? (
          <div className="loading">
            <span className="spinner" /> Loading months…
          </div>
        ) : (
          <DataTable<AdsMonthly>
            columns={columns}
            rows={visibleMonths}
            rowKey={(r) => r.month}
            defaultSortKey="month"
            defaultSortDir="desc"
            emptyMessage={
              emptyMonthCount > 0
                ? "Only empty pre-spend months so far — record a month's spend above."
                : "No ad spend or attributed clients yet — record a month's spend above."
            }
          />
        )}
        <div className="table-footer">
          <span>
            CAC = spend ÷ new Google-Ads clients. Blended CAC = spend ÷ all new clients that month.
          </span>
          {emptyMonthCount > 0 && (
            <label className="checkbox nowrap" title="Months with $0 spend and 0 Google-Ads clients (pre-spend / pre-attribution history)">
              <input
                type="checkbox"
                checked={showEmptyMonths}
                onChange={(e) => setShowEmptyMonths(e.target.checked)}
              />
              Show {emptyMonthCount} empty {emptyMonthCount === 1 ? "month" : "months"}
            </label>
          )}
          <button
            type="button"
            className="btn-link small"
            onClick={() => {
              months.reload();
              gaClients.reload();
              unattributed.reload();
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="panel mt-2">
        <div className="panel-title">Google Ads clients by cohort month</div>
        <p className="muted small">
          Every client attributed to Google Ads, grouped by the month of their first visit — the
          same cohorts the ROAS table counts. Lifetime value is live, so these totals grow as
          cohort clients keep coming back.
        </p>
        {gaClients.error && <div className="error-banner">{gaClients.error}</div>}
        {gaClients.loading && !gaClients.data ? (
          <div className="loading">
            <span className="spinner" /> Loading Google Ads clients…
          </div>
        ) : cohorts.length === 0 ? (
          <div className="empty-state">
            No clients attributed to Google Ads yet — use the “Set acquisition source” bulk action
            on the <Link to="/clients">Clients</Link> list.
          </div>
        ) : (
          cohorts.map((g) => (
            <div key={g.monthKey ?? "no-visit"} className="mt-2">
              <div className="bold">
                {g.monthKey ? monthLabel(g.monthKey) : "Attributed, no recorded visit yet"}
                <span className="muted">
                  {" "}
                  — {g.clients.length} {g.clients.length === 1 ? "client" : "clients"} — cohort
                  lifetime {maskMoney(money(g.lifetime), privacy)}
                </span>
              </div>
              {g.monthKey == null && (
                <p className="muted small">
                  Never served so far (e.g. cancelled or upcoming first bookings), so they belong
                  to no revenue cohort yet.
                </p>
              )}
              <CohortTable clients={g.clients} privacy={privacy} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}
